/**
 * Hover-crosshair tooltip — the HTML/DOM counterpart of the SVG `ChartTooltip`
 * used on the transaction dots, so the crosshair on the profit chart and the
 * candle/RSI stack shares one tooltip design: a dark card with a colour accent
 * bar, a header and aligned label/value rows.
 */

const FONT = 'ui-monospace, SFMono-Regular, Menlo, monospace';

export interface CrosshairRow {
  label: string;
  value: string;
  /** Colours the value — green gain, red loss, neutral otherwise. */
  tone?: 'pos' | 'neg';
  /** Explicit value colour — overrides `tone` when set. */
  color?: string;
}

interface Props {
  title: string;
  /** Header accent colour. */
  accent: string;
  rows: CrosshairRow[];
  /** Absolute position (px) within the chart container. */
  left: number;
  top: number;
  /** Anchor the card to the left of `left` (used near the right edge). */
  flip?: boolean;
}

export const CROSSHAIR_TOOLTIP_WIDTH = 196;

const CrosshairTooltip = ({ title, accent, rows, left, top, flip }: Props) => (
  <div
    style={{
      position: 'absolute',
      left,
      top,
      width: CROSSHAIR_TOOLTIP_WIDTH,
      transform: flip ? 'translateX(-100%)' : 'none',
      marginLeft: flip ? -14 : 14,
      pointerEvents: 'none',
      zIndex: 30,
      background: '#0c0e12',
      border: '1px solid rgba(255,255,255,0.13)',
      borderRadius: 7,
      padding: '9px 13px 10px 15px',
      boxSizing: 'border-box',
      fontFamily: FONT,
    }}
  >
    {/* category accent down the left edge */}
    <div
      style={{
        position: 'absolute',
        left: 0,
        top: 6,
        bottom: 6,
        width: 3,
        borderRadius: 1.5,
        background: accent,
      }}
    />
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        marginBottom: 7,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: accent,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: accent,
          letterSpacing: '0.07em',
        }}
      >
        {title}
      </span>
    </div>
    <div
      style={{
        borderTop: '1px solid rgba(255,255,255,0.09)',
        marginBottom: 6,
      }}
    />
    {rows.map((row, index) => (
      <div
        key={`${row.label}_${index}`}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          padding: '2px 0',
        }}
      >
        <span
          style={{ fontSize: 9.5, color: '#787d85', letterSpacing: '0.03em' }}
        >
          {row.label}
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: row.color
              ? row.color
              : row.tone === 'neg'
              ? '#F0162F'
              : row.tone === 'pos'
              ? '#00B061'
              : '#eef0f2',
          }}
        >
          {row.value}
        </span>
      </div>
    ))}
  </div>
);

export default CrosshairTooltip;
